import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  FormControlLabel,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyIcon from "@mui/icons-material/Key";
import DescriptionIcon from "@mui/icons-material/Description";
import GavelIcon from "@mui/icons-material/Gavel";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useRequestPasswordResetMutation,
  useRequestEmployeePasswordResetMutation,
  useRequestAssistantPasswordResetMutation,
} from "../../../store/api/usersApiSlice";
import useFirebase from "../../../hooks/useFirebase";
import { useSelector } from "react-redux";
import { IOSSwitch } from "../style";
import { useNavigate } from "react-router-dom";
import { KvkkAydinlatmaMetniContent } from "../../../constants/agreements";
import { appColors } from "../../../constants/theme";
import usePreferredLanguage from "../../../hooks/usePreferredLanguage";
import userTypes from "../../../constants/userTypes";
import { isPasswordResetRateLimited } from "../../../utils/passwordResetErrors";

const CustomAccountSettingsMenu = ({ anchorEl, open, handleClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updatePassword } = useFirebase();
  const userEmail = useSelector((state) => state.auth?.user?.email);
  const userTypeUpper = String(
    useSelector((state) => state.auth?.user?.userType || state.auth?.role?.name || "")
  ).toUpperCase();
  const status = useSelector((state) => state.auth?.user.status);
  const [requestPasswordReset] = useRequestPasswordResetMutation();
  const [requestEmployeePasswordReset] = useRequestEmployeePasswordResetMutation();
  const [requestAssistantPasswordReset] = useRequestAssistantPasswordResetMutation();
  const [showKvkkDialog, setShowKvkkDialog] = useState(false);
  const {
    isTurkish,
    syncPreferredLanguage,
    changePreferredLanguage,
  } = usePreferredLanguage();

  useEffect(() => {
    if (!open) return;
    syncPreferredLanguage();
  }, [open]);

  const handleLogout = () => {
    navigate("/logout");
  };

  const handleResetPassword = async () => {
    try {
      if (!userEmail) return;

      const request =
        userTypeUpper === userTypes.DOCTOR
          ? requestPasswordReset
          : userTypeUpper === userTypes.EMPLOYEE
            ? requestEmployeePasswordReset
            : userTypeUpper === userTypes.ASSISTANT
              ? requestAssistantPasswordReset
              : null;

      if (!request) {
        // Backend tarafında patient için reset akışı bu UI repo'sunda yok.
        // Bu yüzden tanınmayan tiplerde mevcut Firebase fallback'ini koruyoruz.
        await updatePassword(userEmail);
        handleClose && handleClose();
        return;
      }

      await request({ email: userEmail }).unwrap();
      toast.success(t("password_reset_email_sent_generic"));
      handleClose && handleClose();
    } catch (err) {
      if (isPasswordResetRateLimited(err)) {
        toast.error(t("password_reset_rate_limited"));
      } else {
        // Backend tarafı generic success döndürmeyi tercih ediyorsa UI da aynı davranışı korur.
        toast.success(t("password_reset_email_sent_generic"));
      }
      handleClose && handleClose();
    }
  };

  const handleLanguageChange = async (event) => {
    event.stopPropagation();
    const wantTurkish = event.target.checked;
    await changePreferredLanguage(wantTurkish ? "tr" : "en");
  };

  const handleOpenKvkkDialog = () => {
    setShowKvkkDialog(true);
    handleClose && handleClose();
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1.2,
            minWidth: { xs: "min(280px, calc(100vw - 24px))", md: 240 },
            maxWidth: { xs: "calc(100vw - 24px)", md: 360 },
            width: { xs: "calc(100vw - 24px)", md: "auto" },
            borderRadius: 2,
            overflow: "visible",
            "& .MuiMenuItem-root": { py: 1 },
            "& .MuiListItemIcon-root": { minWidth: 36 },
            "& .MuiMenuItem-root:hover, & .MuiMenuItem-root:hover .MuiListItemIcon-root": {
              color: appColors.primary,
            },
          },
        }}
        marginThreshold={12}
      >
        <MenuItem
          onClick={() => {
            navigate("/profile");
            handleClose && handleClose();
          }}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: 1.5,
            fontWeight: 600,
            color: appColors.gray900,
            bgcolor: "rgba(13, 148, 136, 0.08)",
            cursor: "pointer",
          }}
        >
          <ListItemIcon>
            <PersonOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          {t("profile")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/settings");
            handleClose && handleClose();
          }}
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: 1.5,
            fontWeight: 600,
            color: appColors.gray900,
            bgcolor: "rgba(13, 148, 136, 0.08)",
            cursor: "pointer",
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          {t("settings")}
        </MenuItem>
        <Box
          sx={{ px: 2, py: 1 }}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => e.stopPropagation()}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Typography sx={{ fontSize: ".9rem", minWidth: 64, textAlign: "left" }}>
              {t("english")}
            </Typography>
            <FormControlLabel
              sx={{ m: 0 }}
              control={
                <IOSSwitch
                  sx={{ m: 0, transform: "scale(0.9)" }}
                  checked={isTurkish}
                  onChange={handleLanguageChange}
                />
              }
              label=""
            />
            <Typography sx={{ fontSize: ".9rem", minWidth: 64, textAlign: "right" }}>
              {t("turkish")}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <MenuItem onClick={handleOpenKvkkDialog}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" />
          </ListItemIcon>
          KVKK Aydınlatma Metni
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate("/sozlesmeler");
            handleClose && handleClose();
          }}
        >
          <ListItemIcon>
            <GavelIcon fontSize="small" />
          </ListItemIcon>
          {t("contracts_page_title", "Sözleşmeler ve Bilgilendirme Metinleri")}
        </MenuItem>
        <Divider />
        {status === "Approved" && (
          <MenuItem onClick={handleResetPassword}>
            <ListItemIcon>
              <KeyIcon fontSize="small" />
            </ListItemIcon>
            {t("reset_password")}
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t("logout")}
        </MenuItem>
      </Menu>

      <Dialog
        open={showKvkkDialog}
        onClose={() => setShowKvkkDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            maxHeight: { xs: "90vh", md: "80vh" },
            m: { xs: 1, md: 0 },
            width: { xs: "calc(100% - 16px)", md: "auto" },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: appColors.primary,
            borderBottom: `1px solid ${appColors.gray200}`,
            pb: 2,
          }}
        >
          KİŞİSEL VERİLERİN İŞLENMESİNE İLİŞKİN AYDINLATMA METNİ
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <KvkkAydinlatmaMetniContent />
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: `1px solid ${appColors.gray200}` }}>
          <Button
            onClick={() => setShowKvkkDialog(false)}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 4,
              "&:hover": {
                background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,
              },
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CustomAccountSettingsMenu;
